import type React from 'react';
import type { ActiveTemplateChoices, TemplateChoiceSelection } from '@/components/case-partner/template-choices';
import type { Message, QuickReplyQuestion, ThinkingStep } from '@/hooks/useChat';
import type { Document } from '@/hooks/useDocuments';
import type { WebCitationDisplay } from '@/utils/citation-utils';
import type { TimelineTextInsertion } from './assistantTurnTimeline';
import type { TimelineWaitMarker } from './assistantTurnTimeline';
import { MessageStream } from './MessageStream';
import type { QuickReplyAnswer } from './QuickReplies';
import { ScrollToBottomButton } from './ScrollToBottomButton';

interface MessagesAreaProps {
  messages: Message[];
  streamingContent: string;
  streamingQuickReplyQuestions?: QuickReplyQuestion[];
  streamingTemplateChoices?: ActiveTemplateChoices | null;
  streamingThinkingSteps: ThinkingStep[];
  streamingTextInsertions?: TimelineTextInsertion[];
  streamingWaitMarkers?: TimelineWaitMarker[];
  isStreaming: boolean;
  catchingUp?: boolean;
  finalAnswerConfirmed?: boolean;
  turnStartedAt?: number | null;
  reasoningDurationMs?: number | null;
  isThinking: boolean;
  stopRequested?: boolean;
  isLoading: boolean;
  documents: Document[] | undefined;
  matterGuid?: string;
  chatGuid?: string | null;
  slug: string;
  copiedMessageIds: Set<string>;
  onCopyMessage: (content: string, id: string | number | undefined) => void;
  onSaveToLibrary?: (content: string) => void;
  scrollAreaRef: React.Ref<HTMLDivElement>;
  contentRef: React.Ref<HTMLDivElement>;
  isScrolledToBottom: boolean;
  isStreamingResponseBeyondFold: boolean;
  onScrollToBottom: () => void;
  streamingResponseRef: React.Ref<HTMLDivElement>;
  /** Render function for cited documents - allows parent to use memoized components */
  renderCitedDocs: (
    content: string,
    webCitationMeta?: ReadonlyMap<string, WebCitationDisplay>,
  ) => React.ReactNode;
  streamingContinuityKey?: string;
  quickReplyDisabled?: boolean;
  onQuickReplySubmit?: (value: string, answers: QuickReplyAnswer[]) => void;
  templateChoiceDisabled?: boolean;
  onTemplateChoiceSubmit?: (value: string, selection: TemplateChoiceSelection) => void;
}

/**
 * Renders the message list area including messages, streaming content, and thinking indicators
 */
export const MessagesArea: React.FC<MessagesAreaProps> = ({
  messages,
  streamingContent,
  streamingQuickReplyQuestions,
  streamingTemplateChoices,
  streamingThinkingSteps,
  streamingTextInsertions = [],
  streamingWaitMarkers = [],
  isStreaming,
  catchingUp = false,
  finalAnswerConfirmed = false,
  turnStartedAt = null,
  reasoningDurationMs = null,
  isThinking,
  stopRequested = false,
  isLoading,
  documents,
  matterGuid,
  chatGuid,
  slug,
  copiedMessageIds,
  onCopyMessage,
  onSaveToLibrary,
  scrollAreaRef,
  contentRef,
  isScrolledToBottom,
  isStreamingResponseBeyondFold,
  onScrollToBottom,
  streamingResponseRef,
  renderCitedDocs,
  streamingContinuityKey,
  quickReplyDisabled = false,
  onQuickReplySubmit,
  templateChoiceDisabled = false,
  onTemplateChoiceSubmit,
}) => {
  return (
    <div className="relative flex-1 bg-white overflow-hidden">
      <div
        className="absolute inset-0 overflow-y-auto px-6 pb-6 pt-0 md:px-8 md:pb-8 lg:px-10 lg:pb-10"
        ref={scrollAreaRef}
      >
        <div className="space-y-8 pt-12 max-w-5xl mx-auto" ref={contentRef}>
          <MessageStream
            messages={messages}
            streamingContent={streamingContent}
            streamingQuickReplyQuestions={streamingQuickReplyQuestions}
            streamingTemplateChoices={streamingTemplateChoices}
            streamingThinkingSteps={streamingThinkingSteps}
            streamingTextInsertions={streamingTextInsertions}
            streamingWaitMarkers={streamingWaitMarkers}
            isStreaming={isStreaming}
            catchingUp={catchingUp}
            finalAnswerConfirmed={finalAnswerConfirmed}
            turnStartedAt={turnStartedAt}
            reasoningDurationMs={reasoningDurationMs}
            isThinking={isThinking}
            stopRequested={stopRequested}
            isLoading={isLoading}
            documents={documents}
            matterGuid={matterGuid}
            chatGuid={chatGuid}
            slug={slug}
            copiedMessageIds={copiedMessageIds}
            onCopyMessage={onCopyMessage}
            onSaveToLibrary={onSaveToLibrary}
            renderCitedDocs={renderCitedDocs}
            streamingContinuityKey={streamingContinuityKey}
            streamingResponseRef={streamingResponseRef}
            quickReplyDisabled={quickReplyDisabled}
            onQuickReplySubmit={onQuickReplySubmit}
            templateChoiceDisabled={templateChoiceDisabled}
            onTemplateChoiceSubmit={onTemplateChoiceSubmit}
          />
        </div>
      </div>

      {!isScrolledToBottom &&
        messages.length > 0 &&
        (!isStreaming || !streamingContent || isStreamingResponseBeyondFold) && (
          <ScrollToBottomButton
            className="absolute bottom-1 left-1/2 z-10 -translate-x-1/2"
            isWorking={isStreaming}
            onClick={onScrollToBottom}
          />
        )}
    </div>
  );
};

export default MessagesArea;
